<script setup lang="ts">
import { useResizeObserver, watchDebounced } from '@vueuse/core';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import type { CVTemplate } from '~/types';
import type { CVData } from '~/types/cv';
import { isPdfTemplate } from '~/constants/cvTemplates';

/*
 * The CV preview: the very PDF the download hands over, printed by the server
 * (server/api/pdf/preview.post.ts, the export's own pipeline) and drawn here
 * page by page with pdf.js. Page breaks, fonts and the watermark are therefore
 * the file's own — nothing is laid out a second time in the browser.
 */

/* Breathing room either side of the sheet. Without it the page is scaled to
   the exact container width, so its frame sits on the clipping edge and reads
   as a CV cut off left and right. */
const PAGE_GUTTER_PX = 16;
/* CSS px per PDF point. */
const PX_PER_PT = 96 / 72;

const props = defineProps<{
  cvData: CVData;
  template: CVTemplate;
  qrCodeData?: string;
  /** Kept for API compatibility; the server decides premium/watermark from the session. */
  isPremium?: boolean;
  /**
   * Paid guest download (no account): the server renders that purchase's own
   * snapshot, clean, so the preview is the file the buyer is about to get.
   */
  guestToken?: string;
  /**
   * A link for one company (/r/<token>): the server renders the CV attached to
   * that application, with its owner's plan deciding the watermark.
   */
  linkToken?: string;
  /**
   * Marketing preview of the free PDF: asks the server for the watermark even
   * when the visitor is entitled to a clean file, so "this is the free preview"
   * is not a claim the page makes about a sheet that has none.
   */
  watermark?: boolean;
  /**
   * Showcase preview: the server renders its own sample CV, clean — `true` for
   * the /demo CV, a slug for a /resume-examples page. `cvData` is then only
   * what the caller shows around the sheet, not what is rendered.
   */
  sample?: boolean | string;
  /** How the sheet is lifted off the desk. Marketing pages use the Sofax hairline. */
  frame?: 'shadow' | 'border';
}>();

const emit = defineEmits<{
  /** The preview PDF was fetched and drawn. */
  (e: 'loaded'): void;
  /** Fetching the preview failed; `message` is safe to show. */
  (e: 'error', message: string): void;
}>();

const { t } = useI18n();

const containerRef = ref<HTMLDivElement | null>(null);
const pagesRef = ref<HTMLDivElement | null>(null);
const isLoading = ref(true);
/** A newer PDF is on its way while the previous one is still shown. */
const isRefreshing = ref(false);
/** The template was switched and the sheet on screen is still the old layout. */
const isSwitching = ref(false);
const loadError = ref<string | null>(null);

let pdfDoc: PDFDocumentProxy | null = null;
let abort: AbortController | null = null;
/** Bumped by every fetch and every draw; an older one that finishes late is dropped. */
let generation = 0;

type PdfJs = typeof import('pdfjs-dist');
let pdfjsPromise: Promise<PdfJs> | null = null;

/* The legacy build: the modern one needs Promise.withResolvers, which Safari
   before 17.4 lacks. Loaded on demand, so pages without a preview never pay for it. */
function loadPdfJs(): Promise<PdfJs> {
  pdfjsPromise ??= Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
  ]).then(([pdfjs, worker]) => {
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
    return pdfjs as unknown as PdfJs;
  });
  return pdfjsPromise;
}

async function fetchPdf(signal: AbortSignal): Promise<ArrayBuffer> {
  if (!isPdfTemplate(props.template)) {
    throw new Error(`Template "${props.template}" has no PDF preview`);
  }

  return await $fetch<ArrayBuffer>('/api/pdf/preview', {
    method: 'POST',
    body: {
      cvData: props.cvData,
      template: props.template,
      locale: props.cvData.cvLanguage,
      qrCodeData: props.qrCodeData,
      guestToken: props.guestToken,
      linkToken: props.linkToken,
      watermark: props.watermark,
      sample: props.sample,
    },
    responseType: 'arrayBuffer',
    signal,
  });
}

/** Draws every page of `doc` at the container's width, then swaps them in at once. */
async function draw(doc: PDFDocumentProxy) {
  const host = pagesRef.value;
  const container = containerRef.value;
  if (!host || !container) return;
  const run = ++generation;

  const available = container.getBoundingClientRect().width - PAGE_GUTTER_PX * 2;
  const dpr = window.devicePixelRatio || 1;
  const sheets: HTMLCanvasElement[] = [];

  for (let number = 1; number <= doc.numPages; number++) {
    const page = await doc.getPage(number);
    const natural = page.getViewport({ scale: 1 });
    // Never upscale past the sheet's real size.
    const cssWidth = Math.max(1, Math.min(available, natural.width * PX_PER_PT));
    const scale = cssWidth / natural.width;
    const viewport = page.getViewport({ scale: scale * dpr });

    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${(natural.height * scale)}px`;
    canvas.className = 'cv-pdf-page';
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', `CV page ${number} of ${doc.numPages}`);

    const context = canvas.getContext('2d');
    if (!context) continue;
    await page.render({ canvasContext: context, viewport }).promise;
    if (run !== generation) return;
    sheets.push(canvas);
  }

  if (run !== generation) return;
  host.replaceChildren(...sheets);
}

// Heuristic: cvData is "ready" when it has at least a name, title, or any
// list content. Empty/default cvData (e.g. before parent's load completes)
// would otherwise produce a flash of an empty CV with just a QR code.
const isDataReady = computed(() => {
  const d = props.cvData;
  if (!d) return false;
  return Boolean(
    d.name?.trim()
    || d.title?.trim()
    || d.aboutMe?.trim()
    || (d.experiences?.length ?? 0) > 0
    || (d.education?.length ?? 0) > 0
  );
});

async function refresh() {
  if (!import.meta.client) return;
  if (!isDataReady.value && !props.sample) {
    // Stay in loading state; the cvData watcher will refresh once data lands.
    isLoading.value = true;
    return;
  }

  abort?.abort();
  const controller = new AbortController();
  abort = controller;
  isRefreshing.value = !isLoading.value;
  loadError.value = null;

  try {
    const [pdfjs, data] = await Promise.all([loadPdfJs(), fetchPdf(controller.signal)]);
    if (controller.signal.aborted) return;
    const doc = await pdfjs.getDocument({ data: new Uint8Array(data) }).promise;
    if (controller.signal.aborted) {
      void doc.destroy();
      return;
    }
    const previous = pdfDoc;
    pdfDoc = doc;
    await draw(doc);
    void previous?.destroy();
    isLoading.value = false;
    emit('loaded');
  } catch (err) {
    if (controller.signal.aborted) return;
    console.error('Failed to load CV preview:', err);
    // Never surface raw fetch/HTTP text to visitors; a translated message + retry is enough.
    // The body of an error comes back as bytes (responseType), so go by status.
    const status = (err as { statusCode?: number })?.statusCode;
    const message = status === 429 ? t('cv.preview.limitReached') : t('cv.preview.loadFailed');
    loadError.value = message;
    isLoading.value = false;
    emit('error', message);
  } finally {
    if (abort === controller) {
      abort = null;
      isRefreshing.value = false;
      isSwitching.value = false;
    }
  }
}

let lastWidth = 0;
useResizeObserver(containerRef, (entries) => {
  const width = Math.round(entries[0]?.contentRect.width ?? 0);
  if (!pdfDoc || !width || width === lastWidth) return;
  lastWidth = width;
  void draw(pdfDoc);
});

onMounted(() => {
  refresh();
});

onBeforeUnmount(() => {
  abort?.abort();
  generation++;
  void pdfDoc?.destroy();
  pdfDoc = null;
});

/* A template switch is one deliberate click, so print it at once and say so:
   the old layout stays on screen until the new one is drawn, and without a
   clear sign the click looks like it did nothing. */
watch(
  () => props.template,
  () => {
    isSwitching.value = !isLoading.value && !loadError.value;
    refresh();
  }
);

/* Every pause in typing prints a PDF on the server, so wait for a real pause. */
watchDebounced(
  () => [props.cvData, props.qrCodeData, props.isPremium, props.guestToken, props.linkToken, props.watermark, props.sample],
  () => refresh(),
  { debounce: 800, deep: true }
);
</script>

<template>
  <div ref="containerRef" class="cv-paged-preview">
    <div v-if="loadError" class="flex flex-col items-center gap-3 p-6 text-center text-sm">
      <div class="text-red-500">{{ loadError }}</div>
      <button
        type="button"
        class="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
        @click="refresh"
      >
        {{ t('cv.preview.retry') }}
      </button>
    </div>
    <div v-else-if="isLoading" class="p-4 text-center text-sm text-gray-400">
      {{ t('common.loadingPreview') }}
    </div>
    <div
      ref="pagesRef"
      class="cv-pdf-pages"
      :class="[`frame-${frame ?? 'shadow'}`, { 'is-refreshing': isRefreshing, 'is-switching': isSwitching, 'opacity-0': isLoading && !loadError }]"
    />
    <Transition name="cv-switch">
      <div v-if="isSwitching" class="cv-switch-overlay">
        <div class="cv-switch-card" role="status" aria-live="polite">
          <LoadingSpinner />
          <p class="cv-switch-title">{{ t('cv.preview.switchingTemplate', { template: t(`cv.templates.${template}.name`) }) }}</p>
          <p class="cv-switch-hint">{{ t('cv.preview.switchingTemplateHint') }}</p>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.cv-paged-preview {
  width: 100%;
  height: 100%;
  position: relative;
  background: transparent;
}

.cv-pdf-pages {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 24px 16px;
  transition: opacity 0.15s ease-out;
}

.cv-pdf-pages.is-refreshing {
  opacity: 0.85;
}

.cv-pdf-pages.is-switching {
  opacity: 0.35;
  filter: blur(1px);
  transition: opacity 0.2s ease-out, filter 0.2s ease-out;
}

/* Covers the whole sheet; the card inside sticks in view however far the
   preview is scrolled. */
.cv-switch-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.cv-switch-card {
  position: sticky;
  top: 30%;
  align-self: flex-start;
  margin-top: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 320px;
  padding: 24px 32px;
  border-radius: 0.625rem;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  text-align: center;
}

.cv-switch-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.cv-switch-hint {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
}

.cv-switch-enter-active,
.cv-switch-leave-active {
  transition: opacity 0.2s ease-out;
}

.cv-switch-enter-from,
.cv-switch-leave-to {
  opacity: 0;
}

.cv-pdf-pages :deep(.cv-pdf-page) {
  display: block;
  background: white;
}

/* How the sheet is separated from the desk behind it. The editor keeps the
   raised look; marketing pages use the Sofax hairline (--line, 0.625rem radius)
   so the preview matches the cards around it. */
.frame-shadow :deep(.cv-pdf-page) {
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.35);
}

.frame-border :deep(.cv-pdf-page) {
  border: 1px solid #EAEDF0;
  border-radius: 0.625rem;
}
</style>
